import React, { createContext, useContext, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';

/**
 * User Activity Tracking Context
 * Tracks feature usage, page views, and user interactions for insights
 */

const ActivityContext = createContext(null);

// Activity types
export const ACTIVITY_TYPES = {
  PAGE_VIEW: 'page_view',
  FEATURE_USE: 'feature_use',
  BUTTON_CLICK: 'button_click',
  FORM_SUBMIT: 'form_submit',
  SEARCH: 'search',
  FILTER: 'filter',
  EXPORT: 'export',
  ERROR: 'error',
  SESSION_START: 'session_start',
  SESSION_END: 'session_end',
};

// Feature categories for analytics
export const FEATURE_CATEGORIES = {
  DASHBOARD: 'dashboard',
  OPERATIONS: 'operations',
  NETWORK: 'network',
  FINANCE: 'finance',
  COMPLIANCE: 'compliance',
  ADMIN: 'admin',
  GOVERNMENT: 'government',
  FLEET: 'fleet',
};

export const ActivityProvider = ({ children }) => {
  const location = useLocation();
  const { user } = useAuth();
  const sessionId = useRef(generateSessionId());
  const activityBuffer = useRef([]);
  const flushTimeout = useRef(null);

  // Generate unique session ID
  function generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Get feature category from path
  const getCategoryFromPath = useCallback((path) => {
    if (path === '/' || path.includes('executive')) return FEATURE_CATEGORIES.DASHBOARD;
    if (path.includes('ops') || path.includes('incident')) return FEATURE_CATEGORIES.OPERATIONS;
    if (path.includes('network') || path.includes('station')) return FEATURE_CATEGORIES.NETWORK;
    if (path.includes('finance') || path.includes('revenue') || path.includes('pricing')) return FEATURE_CATEGORIES.FINANCE;
    if (path.includes('compliance') || path.includes('esg')) return FEATURE_CATEGORIES.COMPLIANCE;
    if (path.includes('admin') || path.includes('rbac')) return FEATURE_CATEGORIES.ADMIN;
    if (path.includes('gov') || path.includes('subsidy') || path.includes('discom')) return FEATURE_CATEGORIES.GOVERNMENT;
    if (path.includes('fleet') || path.includes('host')) return FEATURE_CATEGORIES.FLEET;
    return 'other';
  }, []);

  // Track activity
  const trackActivity = useCallback((type, data = {}) => {
    const activity = {
      id: `act_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
      type,
      timestamp: new Date().toISOString(),
      sessionId: sessionId.current,
      userId: user?.email || 'anonymous',
      userRole: user?.role || 'guest',
      path: location.pathname,
      category: getCategoryFromPath(location.pathname),
      ...data,
      metadata: {
        userAgent: navigator.userAgent,
        screenWidth: window.innerWidth,
        screenHeight: window.innerHeight,
        language: navigator.language,
        ...data.metadata,
      },
    };

    // Add to buffer
    activityBuffer.current.push(activity);

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.log('📊 Activity Tracked:', activity);
    }

    // Store locally
    storeActivityLocally(activity);

    // Schedule flush to backend
    scheduleFlush();

    return activity;
  }, [user, location.pathname, getCategoryFromPath]);

  // Store activity locally (localStorage)
  const storeActivityLocally = (activity) => {
    try {
      const stored = JSON.parse(localStorage.getItem('urgaa_activities') || '[]');
      stored.push(activity);
      // Keep only last 1000 activities
      if (stored.length > 1000) {
        stored.splice(0, stored.length - 1000);
      }
      localStorage.setItem('urgaa_activities', JSON.stringify(stored));
    } catch (e) {
      console.warn('Failed to store activity locally:', e);
    }
  };

  // Schedule flush to backend
  const scheduleFlush = useCallback(() => {
    if (flushTimeout.current) {
      clearTimeout(flushTimeout.current);
    }
    
    flushTimeout.current = setTimeout(() => {
      flushActivities();
    }, 5000); // Flush every 5 seconds
  }, []);

  // Flush activities to backend
  const flushActivities = useCallback(async () => {
    if (activityBuffer.current.length === 0) return;

    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    
    // Skip backend sync if user is not authenticated
    // Activities are still stored locally and will sync after login
    if (!token) {
      if (process.env.NODE_ENV === 'development') {
        console.log('📊 Skipping analytics sync - user not authenticated');
      }
      activityBuffer.current = []; // Clear buffer since they're already in localStorage
      return;
    }

    const activitiesToSend = [...activityBuffer.current];
    activityBuffer.current = [];

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      await fetch(`${backendUrl}/api/analytics/activities`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ activities: activitiesToSend }),
      });
    } catch (e) {
      // Re-add to buffer on failure
      activityBuffer.current = [...activitiesToSend, ...activityBuffer.current];
      console.warn('Failed to flush activities:', e);
    }
  }, []);

  // Track page views
  useEffect(() => {
    trackActivity(ACTIVITY_TYPES.PAGE_VIEW, {
      page: location.pathname,
      title: document.title,
    });
  }, [location.pathname, trackActivity]);

  // Track session start
  useEffect(() => {
    trackActivity(ACTIVITY_TYPES.SESSION_START);

    // Track session end on unload
    const handleUnload = () => {
      trackActivity(ACTIVITY_TYPES.SESSION_END);
      flushActivities();
    };

    window.addEventListener('beforeunload', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      handleUnload();
    };
  }, [trackActivity, flushActivities]);

  // Sync buffered activities when user logs in
  useEffect(() => {
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    if (token && user) {
      // User just logged in, flush any pending activities
      if (activityBuffer.current.length > 0) {
        flushActivities();
      }
    }
  }, [user, flushActivities]);

  // Convenience methods
  const trackFeatureUse = useCallback((featureName, data = {}) => {
    return trackActivity(ACTIVITY_TYPES.FEATURE_USE, { featureName, ...data });
  }, [trackActivity]);

  const trackButtonClick = useCallback((buttonName, data = {}) => {
    return trackActivity(ACTIVITY_TYPES.BUTTON_CLICK, { buttonName, ...data });
  }, [trackActivity]);

  const trackFormSubmit = useCallback((formName, data = {}) => {
    return trackActivity(ACTIVITY_TYPES.FORM_SUBMIT, { formName, ...data });
  }, [trackActivity]);

  const trackSearch = useCallback((query, results = 0) => {
    return trackActivity(ACTIVITY_TYPES.SEARCH, { query, resultsCount: results });
  }, [trackActivity]);

  const trackError = useCallback((error, data = {}) => {
    return trackActivity(ACTIVITY_TYPES.ERROR, { 
      errorMessage: error.message || error,
      errorStack: error.stack,
      ...data 
    });
  }, [trackActivity]);

  // Get activity summary
  const getActivitySummary = useCallback(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('urgaa_activities') || '[]');
      const summary = {
        totalActivities: stored.length,
        pageViews: stored.filter(a => a.type === ACTIVITY_TYPES.PAGE_VIEW).length,
        featureUses: stored.filter(a => a.type === ACTIVITY_TYPES.FEATURE_USE).length,
        errors: stored.filter(a => a.type === ACTIVITY_TYPES.ERROR).length,
        byCategory: {},
        byPage: {},
        recentActivities: stored.slice(-20),
      };

      stored.forEach(activity => {
        // By category
        if (!summary.byCategory[activity.category]) {
          summary.byCategory[activity.category] = 0;
        }
        summary.byCategory[activity.category]++;

        // By page
        if (!summary.byPage[activity.path]) {
          summary.byPage[activity.path] = 0;
        }
        summary.byPage[activity.path]++;
      });

      return summary;
    } catch (e) {
      return null;
    }
  }, []);

  const value = {
    trackActivity,
    trackFeatureUse,
    trackButtonClick,
    trackFormSubmit,
    trackSearch,
    trackError,
    getActivitySummary,
    sessionId: sessionId.current,
    ACTIVITY_TYPES,
    FEATURE_CATEGORIES,
  };

  return (
    <ActivityContext.Provider value={value}>
      {children}
    </ActivityContext.Provider>
  );
};

export const useActivity = () => {
  const context = useContext(ActivityContext);
  if (!context) {
    throw new Error('useActivity must be used within an ActivityProvider');
  }
  return context;
};

export default ActivityContext;
